import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import * as Yup from 'yup';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Backdrop, Box, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, FormLabel, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from "formik";
import { ALERT_TYPE_TYPE } from '../../types/common.type';
import { ADD_NOTIFICATION } from '../../store/types';
import { useDispatch, useSelector } from 'react-redux';
import { createCustomAlert } from '../../services/custom-alert.service';
import { getAlertsReports } from '../../services/alert-report.service';
import _ from 'lodash';
// import AlertDialog from './AlertDialog';
import useCardFilterAndTransferType from '../hooks/useCardFilterAndTransferType';
import ShareIcon from '@mui/icons-material/Share';
import { IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import ShareAlertReportDialog from './ShareAlertReportDialog';
import ReportSummary from './ReportSummary';
import { generateRichReportData } from '../../utilis/report-description';
import useIsMobileDevice from '../../hooks/useIsMobileDevice';

export default function AlertReportCustomDialog({
    handleClose,
    pageType,
    cardType,
    totalCardType = null,
    customCardData
}) {
    const isMobile = useIsMobileDevice();
    const { facilities: activeFacilities } = useSelector((state) => state.activeFacilities);
    const headerFilterItems = useSelector((state) => state.common.headerFilterItems);

    const { cardFilter, transferType } = useCardFilterAndTransferType(pageType);

    const [existingAlerts, setExistingAlerts] = React.useState();
    // const [isChangesDetected, setIsChangesDetected] = React.useState(null);
    const [showShareDialog, setShowShareDialog] = useState(false);


    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(true);

    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Name is required')
    });

    /* eslint-disable no-unused-vars */
    const [initialValues, setInitialValues] = React.useState({
        alertType: [ALERT_TYPE_TYPE.WEEKS],
        isRising: false,
        isDropping: false,
        name: "",
        id: null,
        description: "",
        cardType: null  // ✅ Add cardType to track AI-provided card type
    });

    // CRITICAL: Use AI-provided cardType if available, otherwise use prop
    const activeCardType = initialValues.cardType || cardType;
    
    // Track if initial values have been applied to prevent resetting user changes
    const hasAppliedInitialValues = React.useRef(false);

    // Handle AI pre-filled alert data
    React.useEffect(() => {
        // Expose dialog trigger globally for AI to use
        window.alertDialogTrigger = (aiAlertData) => {                
            setInitialValues(prevState => ({
                ...prevState,
                ...aiAlertData
            }));
            hasAppliedInitialValues.current = false; // Reset flag when new AI data comes in
        };

        // Check for AI pre-filled data on mount
        if (window.aiPrefilledAlertData) {
            setInitialValues(prevState => ({
                ...prevState,
                ...window.aiPrefilledAlertData
            }));
            window.aiPrefilledAlertData = null; // Clear after use
            hasAppliedInitialValues.current = false; // Reset flag
        }

        return () => {
            // Cleanup
            window.alertDialogTrigger = null;
        };
    }, []);

    const formik = useFormik({
        enableReinitialize: false, // Changed to false to prevent resetting user changes
        initialValues: {
            ...initialValues
        },
        validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            handleSave(values);
            setSubmitting(false);
        },
        validateOnChange: true,
        validateOnBlur: true,
    });

    // Handle AI pre-filled data updates to formik after it's initialized
    React.useEffect(() => {
        // Only apply initial values once or when they change externally (from AI)
        // Don't reset if user has already changed the values
        if (!hasAppliedInitialValues.current) {
            if (initialValues.alertType) {
                formik.setFieldValue("alertType", initialValues.alertType, false);
            }
            if (initialValues.isRising !== undefined) {
                formik.setFieldValue("isRising", initialValues.isRising, false);
            }
            if (initialValues.isDropping !== undefined) {
                formik.setFieldValue("isDropping", initialValues.isDropping, false);
            }
            if (initialValues.name) {
                formik.setFieldValue("name", initialValues.name, false);
            }
            hasAppliedInitialValues.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues]); // Only run when initialValues change (from AI data) - formik methods are stable



    const compareAlertSettings = (valuesData, existingAlerts) => {
        // Compare only the specific fields
        const fieldsToCompare = ['alertType', 'isRising', 'isDropping'];

        let hasChanges = false;

        // Loop through each field and compare
        fieldsToCompare.forEach(field => {
            if (JSON.stringify(valuesData[field]) !== JSON.stringify(existingAlerts[field])) {
                hasChanges = true;
            }
        });

        return hasChanges;
    };

    const handleSave = React.useCallback(async (valuesData, isSkipValidation = false) => {
        try {
            // let hasChanges = await compareAlertSettings(valuesData, { ...existingAlerts, alertType: existingAlerts?.type ?? null });

            // if (hasChanges && !isSkipValidation) {
            //     setIsChangesDetected(valuesData);
            //     return false;
            // }

            // setIsChangesDetected(null);

            formik.setSubmitting(true);

            // Generate rich description data
            const richDescriptionData = generateRichReportData({
                reportOptions: {
                    type: valuesData?.alertType,
                    reportFileType: 'ALERT',
                },
                pageType,
                title: `${_.startCase(pageType)} Alert`,
                headerFilterItems,
                selectedColumns: [],
                tableFilterType: 'all',
                cardType: activeCardType  // ✅ Use active card type
            });

            const updateData = {
                alertReportId: valuesData?.id,
                name: valuesData?.name,
                page: pageType,
                activeFacilities: activeFacilities,
                alertConfig: {
                    type: valuesData?.alertType,
                    isRising: valuesData?.isRising,
                    isDropping: valuesData?.isDropping,
                },
                description: JSON.stringify(richDescriptionData),
                filter: {
                    cardFilter,
                    transferType,
                    cardType: activeCardType,  // ✅ Use active card type
                    ...(customCardData ? {
                        alertTypeId: customCardData._id,
                        alertType: customCardData.isCustomTab ? "customTab" : "question",
                        refSlug: customCardData?.accessor
                    } : {}),
                    ...(totalCardType ? { totalCardType } : {})
                },
                cardFilter: cardFilter,
                transferType: transferType,
                ...(customCardData ? {
                    alertTypeId: customCardData._id,
                    alertType: customCardData.isCustomTab ? "customTab" : "question",
                    refSlug: customCardData?.accessor
                } : {}),
                ...(totalCardType ? { totalCardType } : {})
            };

            const res = await createCustomAlert(updateData);
            formik.setSubmitting(false);
            if (res) {
                formik.resetForm();
                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: {
                        type: "success",
                        label: "Alert settings updated successfully",
                        id: "alertSettingsUpdated",
                    },
                });
                handleClose();
            }
        } catch (error) {
            // Check for backend duplicate name error
            if (
                error?.response?.data?.status === 400 &&
                error?.response?.data?.message === "Custom alert name already exists"
            ) {
                formik.setFieldError('name', 'Custom alert name already exists');
                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: {
                        type: "error",
                        label: "Custom alert name already exists. Please choose a different name.",
                        id: "duplicateCustomAlertName",
                    },
                });
            } else {
                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: {
                        type: "error",
                        label: "Failed to save alert settings",
                        id: "alertSettingsSaveError",
                    },
                });
            }
            formik.setSubmitting(false);
        }
    }, [formik, pageType, headerFilterItems, activeCardType, activeFacilities, cardFilter, transferType, customCardData, totalCardType, handleClose, dispatch]);

    const getAlertsReportsDB = React.useCallback(async (facilityId = null) => {
        setLoading(true);
        try {
            const res = await getAlertsReports({
                page: pageType,
                facilityId,
            });

            if (res) {
                setExistingAlerts(res);
                setLoading(false);
                formik?.setFieldValue("id", res?._id);
                formik?.setFieldValue("alertType", res?.type);
                formik?.setFieldValue("isRising", res?.isRising);
                formik?.setFieldValue("isDropping", res?.isDropping);
                formik?.setFieldValue("type", res?.type);
            } else {
                formik?.setFieldValue("alertType", [ALERT_TYPE_TYPE.WEEKS]);
                formik?.setFieldValue("isRising", false);
                formik?.setFieldValue("isDropping", false);
                formik?.setFieldValue("id", null);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            dispatch({
                type: ADD_NOTIFICATION,
                payload: {
                    type: "error",
                    label: "Failed to load alert settings 2",
                    id: "loadSettingsError",
                },
            });
        } finally {
            setLoading(false);
        }
    }, [pageType, formik, dispatch]);

    // Track if we've loaded data from API to prevent overwriting user changes
    const hasLoadedFromAPI = React.useRef(false);
    
    React.useEffect(() => {
        // Only load from API if we haven't loaded yet, or if activeFacilities changed for the first time
        if (!hasLoadedFromAPI.current) {
            getAlertsReportsDB(activeFacilities?.[0]);
            hasLoadedFromAPI.current = true;
        }
    }, [activeFacilities]); // eslint-disable-line react-hooks/exhaustive-deps



    const AlertTypeSelect = ({ formik }) => {
        return (
            <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel id="alert-type-label">Alert frequency</FormLabel>
                <ToggleButtonGroup
                    value={formik.values.alertType}
                    onChange={(event, newAlertType) => {
                        if (newAlertType !== null) {
                            formik.setFieldValue("alertType", newAlertType);
                        }
                    }}
                    aria-labelledby="alert-type-label"
                    color="primary"
                    sx={{ display: 'flex' }}
                    size="large"
                >
                    <ToggleButton sx={{ padding: '8px 16px', ...(isMobile && {fontSize: "14px"}) }} value={ALERT_TYPE_TYPE.WEEKS}>Weekly</ToggleButton>
                    <ToggleButton sx={{ padding: '8px 16px', ...(isMobile && {fontSize: "14px"}) }} value={ALERT_TYPE_TYPE.BI_WEEKS}>Bi-Weekly</ToggleButton>
                    <ToggleButton sx={{ padding: '8px 16px', ...(isMobile && {fontSize: "14px"}) }} value={ALERT_TYPE_TYPE.MONTHS}>Monthly</ToggleButton>
                </ToggleButtonGroup>
            </FormControl>
        );
    };

    const renderConfigurationTab = () => (
        <form onSubmit={formik.handleSubmit}>
            <FormGroup>
                <AlertTypeSelect formik={formik} />

                <Box display="flex" justifyContent="flex-start" alignItems="center" sx={{ mb: 2, ml: 0.2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="isRising"
                                checked={formik.values.isRising}
                                onChange={formik.handleChange}
                            />
                        }
                        // label="Are Rising"
                        label="Trending up"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="isDropping"
                                checked={formik.values.isDropping}
                                onChange={formik.handleChange}
                            />
                        }
                        // label="Are Dropping"
                        label="Trending down"
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        name="name"
                        label="Name"
                        variant="outlined"
                        fullWidth
                        value={formik.values.name}
                                            onChange={(e) => {
                        // Clear duplicate name error when user starts typing
                        if (formik.errors.name === 'Custom alert name already exists') {
                            formik.setFieldError('name', undefined);
                        }
                        formik.handleChange(e);
                    }}
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.name && formik.errors.name}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                    />
                </Box>

                {/* Alert Configuration Summary */}
                <Box sx={{ mt: 2 }}>
                    <ReportSummary
                        reportOptions={{
                            type: formik.values.alertType,
                            reportFileType: 'ALERT',
                        }}
                        pageType={pageType}
                        title={`${_.startCase(pageType)} Alert`}
                        selectedColumns={[]}
                        tableFilterType="all"
                        showTitle={true}
                        compact={false}
                        cardType={activeCardType}  // ✅ Use active card type from AI or prop
                    />
                </Box>
            </FormGroup>
        </form>
    );

    const renderContent = () => (
        <Box>
            {/* Configuration Content */}
            <Box sx={{ mt: 2 }}>
                {renderConfigurationTab()}
            </Box>
        </Box>
    );

    return (
        <>
            <Dialog fullWidth maxWidth="md" open={true} onClose={handleClose}>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <span>{_.capitalize(pageType)} Alerts</span>
                        {existingAlerts?._id && (
                            <Tooltip title="Share Alert Settings" arrow>
                                <IconButton
                                    onClick={() => setShowShareDialog(true)}
                                    size="small"
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.light',
                                            color: 'white',
                                        },
                                    }}
                                >
                                    <ShareIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {loading && (
                        <Backdrop open={true} style={{
                            position: 'absolute', top: 0, left: 0, zIndex: 5,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)', // Slightly translucent
                            backdropFilter: 'blur(0.5px)', // Applies a blur effect
                        }}>
                            <CircularProgress color="primary" />
                        </Backdrop>
                    )}
                    {renderContent()}
                </DialogContent>
                <DialogActions>
                    <Box flex="1" display="flex" justifyContent="flex-end">
                        <Button onClick={handleClose} variant="outlined">Close</Button>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            onClick={formik.submitForm}
                            sx={{ marginLeft: 2 }}
                            loading={formik.isSubmitting}
                        >
                            Save
                        </LoadingButton>
                    </Box>
                </DialogActions>
            </Dialog>
            {/* {!!isChangesDetected &&
                <AlertDialog
                    handleClose={() => setIsChangesDetected(null)}
                    content={`The changes you made to will be apply for all alert setting.`}
                    title="Warning"
                    handleSubmit={() => handleSave(isChangesDetected, true)}
                    isConfirm={true}
                    onlyOkay={false}
                />
            } */}
            {showShareDialog && existingAlerts?._id && (
                <ShareAlertReportDialog
                    alertReport={existingAlerts}
                    onClose={() => setShowShareDialog(false)}
                    onShareSuccess={() => {
                        setShowShareDialog(false);
                    }}
                />
            )}
        </>
    );
}
